/**
 * Planner co-branding band (task #618).
 *
 * An Event Planner's business identity, shown to GUESTS above the "Powered by
 * QuikRSVP" line on the RSVP form and the event website. The QuikRSVP line
 * always stays: this is co-branding, not white-labelling.
 *
 * Include the script on a public page and give it something to hang from:
 *
 *   <script src="/assets/js/planner-cobrand.js"
 *           data-form-id="event-abcd"></script>
 *   <script src="/assets/js/planner-cobrand.js"
 *           data-site="sarah-and-mike"></script>
 *
 * or drive it yourself: window.PlannerCobrand.mount({ formId, site, target }).
 *
 * Three rules it must never break, in order of how badly they would show:
 *   1. It NEVER blocks or delays the page. One fetch, after load, and a silent
 *      no-op on any failure — a guest who cannot RSVP because a logo would not
 *      load is a far worse outcome than no logo.
 *   2. It renders nothing at all unless the server says there is branding. The
 *      server decides (subscription, per-event choice); this file does not
 *      second-guess it.
 *   3. Every value is escaped and every link is checked to be http(s) before it
 *      reaches an href or an img src — the strings come from a planner typing
 *      into a form, and they land on a page strangers read.
 */
(function () {
  'use strict';

  var MOUNTED = false;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** '' unless this really is an http(s) URL. Belt and braces: the server
   *  validates on the way in, this validates on the way out. */
  function safeUrl(raw) {
    var v = String(raw == null ? '' : raw).trim();
    if (!v) return '';
    try {
      var u = new URL(v, window.location.origin);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
      return u.href;
    } catch (e) {
      return '';
    }
  }

  function injectStyles() {
    if (document.getElementById('planner-cobrand-styles')) return;
    var st = document.createElement('style');
    st.id = 'planner-cobrand-styles';
    // Theme-AGNOSTIC on purpose. The first version hard-coded light greys and
    // then flipped them under `@media (prefers-color-scheme: dark)` — but the
    // RSVP form and the dashboard are a white card whatever the OS is set to,
    // so on a machine in dark mode the planner's name came out #e5e7eb on white
    // and was effectively invisible (task #618 round 2; it is what the "the
    // branding is missing" screenshot was half showing). Everything now
    // inherits the page's own text colour and separates itself with opacity and
    // neutral greys, which works on a white card and on a dark wedding site
    // without either needing to declare which it is.
    st.textContent = [
      '.planner-cobrand{margin:0 auto .5rem;padding:.6rem .9rem;max-width:520px;',
      'border-top:1px solid rgba(128,128,128,.22);font-size:.82rem;line-height:1.45;',
      'color:inherit;text-align:center}',
      // "Planned by {logo} Company Name" on ONE line, deliberately the same
      // shape as the "Powered by {logo} QuikRSVP" line it sits above (task
      // #643). The logo used to be a 34px block floated to the left of the
      // whole body, which on a narrow footer wrapped into a big square sitting
      // on top of the name and read as a stray image rather than a wordmark.
      '.planner-cobrand-line{display:flex;align-items:center;justify-content:center;',
      'gap:.4rem;flex-wrap:wrap}',
      // Sized in em, so the mark always matches the text beside it rather than
      // a fixed pixel height that is right on one page and wrong on the next.
      '.planner-cobrand-logo{height:1.6em;width:auto;max-width:120px;object-fit:contain;',
      'border-radius:3px;flex:0 0 auto;display:block}',
      '.planner-cobrand-prefix{opacity:.75}',
      '.planner-cobrand-body{min-width:0}',
      '.planner-cobrand-name{font-weight:600;color:inherit}',
      '.planner-cobrand-desc{margin-top:1px;font-size:.76rem;opacity:.7}',
      '.planner-cobrand-links{margin-top:2px;display:flex;gap:.6rem;flex-wrap:wrap;justify-content:center}',
      '.planner-cobrand-links a{color:inherit;opacity:.75;text-decoration:none;font-size:.76rem}',
      '.planner-cobrand-links a:hover{opacity:1;text-decoration:underline}',
      // The compact header chip (round 2): the planner's mark beside the page
      // title, so a client knows whose event this is at a glance without
      // scrolling to the footer.
      // Text leads the chip now ("Planned by …"), so the padding is even on
      // both sides — it no longer has to hug a logo on the leading edge.
      '.planner-cobrand-chip{display:inline-flex;align-items:center;gap:.3rem;',
      'margin-inline-start:.55rem;padding:.15rem .6rem;border-radius:999px;',
      'background:rgba(128,128,128,.18);font-size:.78rem;font-weight:600;color:inherit;',
      'vertical-align:middle;max-width:min(60vw,320px);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      // No logo is the COMMON case, not an error state — the chip simply loses
      // its image and reads as "Planned by Name".
      '.planner-cobrand-chip.is-textonly{gap:.3rem}',
      // contain, not cover (task #643). A planner's logo is almost always a
      // wide wordmark; cropping it into an 18px circle cut the name out of the
      // middle of it and left the rest looking squashed. It keeps its own
      // aspect ratio now, on a white plate so a dark mark stays readable on the
      // dark navbar the chip usually sits in.
      '.planner-cobrand-chip-logo{height:20px;width:auto;max-width:64px;object-fit:contain;',
      'border-radius:3px;flex:0 0 auto;background:#fff;padding:1px 2px;display:block}',
      '.planner-cobrand-chip-prefix{font-weight:400;opacity:.75}',
      // When the planner has a website the whole chip is wrapped in an <a>, and
      // that anchor becomes the chip's only flex item. It has to be a flex
      // container in its own right, or the logo (a block box inside an inline
      // anchor) forces a line break either side of itself and the chip stacks
      // into three lines. Caught on screen, not in the DOM assertions.
      '.planner-cobrand-chip a{color:inherit;text-decoration:none;display:inline-flex;',
      'align-items:center;gap:.3rem;min-width:0;white-space:nowrap}',
      '.planner-cobrand-chip a:hover{text-decoration:underline}'
    ].join('');
    document.head.appendChild(st);
  }

  // Handles, addresses, hosts and phone numbers are left-to-right text. On a
  // Hebrew/Arabic page they were laid out in the RTL paragraph, so "@studio"
  // showed as "studio@" and "+972…" as "…972+".
  function ltr(text) {
    return '<bdi dir="ltr">' + esc(text) + '</bdi>';
  }

  function linkHtml(href, icon, label) {
    var safe = safeUrl(href);
    if (!safe) return '';
    return '<a href="' + esc(safe) + '" target="_blank" rel="noopener noreferrer nofollow">'
      + '<i class="bi ' + icon + '"></i> ' + ltr(label) + '</a>';
  }

  function render(b, plannedByLabel) {
    var links = [];
    links.push(linkHtml(b.website, 'bi-globe2', prettyHost(b.website)));
    if (b.instagram) {
      links.push(linkHtml('https://instagram.com/' + encodeURIComponent(b.instagram),
        'bi-instagram', '@' + b.instagram));
    }
    if (b.contactEmail) {
      // mailto is built here rather than passed through safeUrl, which only
      // allows http(s) — an address is escaped and cannot carry a scheme.
      links.push('<a href="mailto:' + esc(b.contactEmail) + '">'
        + '<i class="bi bi-envelope"></i> ' + ltr(b.contactEmail) + '</a>');
    }
    if (b.phone) {
      links.push('<a href="tel:' + esc(String(b.phone).replace(/[^\d+]/g, '')) + '">'
        + '<i class="bi bi-telephone"></i> ' + ltr(b.phone) + '</a>');
    }
    var logo = safeUrl(b.logoUrl);
    var name = String(b.companyName || '').trim();
    // Nothing worth a band: no name, no logo, no links.
    if (!name && !logo && !links.filter(Boolean).length) return null;

    var el = document.createElement('div');
    el.className = 'planner-cobrand';
    el.setAttribute('data-planner-cobrand', '1');
    // "Planned by {logo} Name". With no name there is nothing to introduce, so
    // the prefix is dropped and a lone logo is simply shown.
    var logoImg = logo
      ? '<img class="planner-cobrand-logo" src="' + esc(logo) + '" alt="" loading="lazy">' : '';
    var line = (name || logo)
      // The single spaces between the spans are deliberate: whitespace-only
      // text nodes are dropped by flex layout (the gap does the spacing), but
      // they survive into textContent, so the line still reads and COPIES as
      // "Planned by Rose & Co. Events" rather than running the words together.
      ? '<div class="planner-cobrand-line">'
        + (name ? '<span class="planner-cobrand-prefix">' + esc(plannedByLabel) + '</span> ' : '')
        + logoImg
        + (name ? '<span class="planner-cobrand-name">' + esc(name) + '</span>' : '')
        + '</div>'
      : '';
    el.innerHTML = '<div class="planner-cobrand-body">'
      + line
      + (b.description ? '<div class="planner-cobrand-desc">' + esc(b.description) + '</div>' : '')
      + (links.filter(Boolean).length
        ? '<div class="planner-cobrand-links">' + links.filter(Boolean).join('') + '</div>' : '')
      + '</div>';
    // A logo that 404s must not leave a torn image icon in the footer, and must
    // not leave a gap between "Planned by" and the name either.
    var bandImg = el.querySelector('img.planner-cobrand-logo');
    if (bandImg) bandImg.addEventListener('error', function () { bandImg.remove(); });
    return el;
  }

  /**
   * The compact form of the same thing, for a page HEADER (round 2): the
   * planner's logo as a small round mark plus their company name, beside the
   * title, so a client sees whose event this is without scrolling.
   *
   * A missing logo is the ordinary case rather than a failure — most planners
   * fill in a name long before they upload anything — so the chip simply drops
   * the image and closes the gap. It never renders a placeholder or a broken
   * <img>, and with no company name there is nothing to show at all.
   */
  function renderChip(b, plannedByLabel) {
    var name = String(b.companyName || '').trim();
    if (!name) return null;
    var logo = safeUrl(b.logoUrl);
    var site = safeUrl(b.website);
    var el = document.createElement('span');
    el.className = 'planner-cobrand-chip' + (logo ? '' : ' is-textonly');
    el.setAttribute('data-planner-cobrand-chip', '1');
    el.title = plannedByLabel + ' ' + name;
    // The chip says the whole thing now — "Planned by {logo} Name" (task #643).
    // It used to show the bare company name and hide "Planned by" in a tooltip,
    // which on a page titled "Party — Responses" read as a second event name.
    var inner = '<span class="planner-cobrand-chip-prefix">' + esc(plannedByLabel) + '</span> '
      + (logo
        ? '<img class="planner-cobrand-chip-logo" src="' + esc(logo) + '" alt="" loading="lazy">'
        : '') + '<span>' + esc(name) + '</span>';
    el.innerHTML = site
      ? '<a href="' + esc(site) + '" target="_blank" rel="noopener noreferrer nofollow">' + inner + '</a>'
      : inner;
    // A logo that 404s must not leave a torn image icon next to the title.
    var img = el.querySelector('img');
    if (img) {
      img.addEventListener('error', function () {
        img.remove();
        el.classList.add('is-textonly');
      });
    }
    return el;
  }

  /** "quikrsvp.com" reads better in a footer than the whole URL. */
  function prettyHost(url) {
    var safe = safeUrl(url);
    if (!safe) return '';
    try {
      return new URL(safe).host.replace(/^www\./, '');
    } catch (e) {
      return safe;
    }
  }

  /**
   * "Planned by", in the page's language. Kept here rather than in the
   * translation packs because this file is loaded on pages that have different
   * translation systems (the form's, the site's) and must not depend on either
   * having finished loading.
   */
  var PLANNED_BY = {
    en: 'Planned by', es: 'Organizado por', fr: 'Organisé par', de: 'Geplant von',
    pt: 'Organizado por', he: 'הופק על ידי', ar: 'من تنظيم', ru: 'Организатор'
  };

  // Task #838: a Corporate event is HOSTED by its organization, not planned
  // by a hired planner; the server says which (data.by === 'organization').
  var HOSTED_BY = {
    en: 'Hosted by', es: 'Organizado por', fr: 'Organisé par', de: 'Veranstaltet von',
    pt: 'Organizado por', he: 'בהפקת', ar: 'تستضيفها', ru: 'Организатор'
  };

  function plannedBy(by) {
    var lang = (document.documentElement.getAttribute('lang') || 'en').slice(0, 2).toLowerCase();
    var table = by === 'organization' ? HOSTED_BY : PLANNED_BY;
    return table[lang] || table.en;
  }

  /**
   * The label is read when the branding arrives, which on the RSVP form is
   * usually before the form has set <html lang> from the guest's language —
   * so an Arabic page kept "Planned by" in English, and a guest switching
   * language kept the first one. Re-label whenever <html lang> changes.
   */
  var LANG_OBSERVED = false;
  function followPageLanguage(by) {
    var relabel = function () {
      var label = plannedBy(by);
      document.querySelectorAll('.planner-cobrand-prefix, .planner-cobrand-chip-prefix')
        .forEach(function (el) { el.textContent = label; });
    };
    relabel();
    if (LANG_OBSERVED || typeof MutationObserver === 'undefined') return;
    LANG_OBSERVED = true;
    new MutationObserver(relabel).observe(document.documentElement,
      { attributes: true, attributeFilter: ['lang'] });
  }

  /**
   * Where the band goes: immediately BEFORE the "Powered by QuikRSVP" line, so
   * the planner is named first and QuikRSVP still closes the page.
   */
  function findAnchor(explicit) {
    if (explicit) {
      var byId = typeof explicit === 'string' ? document.querySelector(explicit) : explicit;
      if (byId) return byId;
    }
    return document.querySelector('.footer-powered-by')
      || document.querySelector('.form-footer')
      || document.querySelector('.site-footer')
      || null;
  }

  /**
   * @param {object} opts
   *   formId | site      — which event's branding to ask for
   *   target             — where the footer band goes (before this element)
   *   headerTarget       — optional: an element to append the compact chip to,
   *                        e.g. the dashboard's title. One fetch feeds both.
   */
  function mount(opts) {
    if (MOUNTED) return;
    var o = opts || {};
    var query = o.formId ? ('formId=' + encodeURIComponent(o.formId))
      : o.site ? ('site=' + encodeURIComponent(o.site)) : '';
    if (!query) return;
    var anchor = findAnchor(o.target);
    var header = o.headerTarget
      ? (typeof o.headerTarget === 'string' ? document.querySelector(o.headerTarget) : o.headerTarget)
      : null;
    // Nowhere to put anything — don't spend the request.
    if (!anchor && !header) return;
    MOUNTED = true;

    fetch('/api/public/planner-branding?' + query)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.branding) return;
        var label = plannedBy(data.by);
        var band = anchor ? render(data.branding, label) : null;
        var chip = header ? renderChip(data.branding, label) : null;
        if (!band && !chip) return;
        injectStyles();
        // The footer keeps the full block; the header gets the short form. Both,
        // not either — a client scanning the page and a client reading it to the
        // end should both know who is running the event.
        if (band) anchor.parentNode.insertBefore(band, anchor);
        if (chip && !header.querySelector('[data-planner-cobrand-chip]')) header.appendChild(chip);
        followPageLanguage(data.by);
      })
      .catch(function () { /* a missing logo must never break an RSVP */ });
  }

  window.PlannerCobrand = { mount: mount };

  // Self-start from the script tag's own data attributes, after the page has
  // finished what it was doing.
  var self = document.currentScript;
  var formId = self && self.getAttribute('data-form-id');
  var site = self && self.getAttribute('data-site');
  if (formId || site) {
    var go = function () { mount({ formId: formId, site: site }); };
    if (document.readyState === 'complete') setTimeout(go, 0);
    else window.addEventListener('load', go);
  }
})();
